import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotificationCompatibility } from '../hooks/useNotificationCompatibility';
import { useNotificationStateManager } from '../hooks/useNotificationStateManager';
import { headerService } from '../services/headers';
import { API_CONFIG } from '../config/api';
import { 
  waitForServiceWorker, 
  sendTokenToBackend,
  checkBackendNotificationStatus
} from '../utils/notificationUtils';

// Función exportable para disparar el mismo toggle desde fuera del componente
export const triggerNotificationToggle = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('notification-toggle'));
  }
};

interface NotificationToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const NotificationToggle: React.FC<NotificationToggleProps> = ({
  className = '',
  size = 'md',
  showLabel = false
}) => {
  const { isWaiter, user } = useAuth();
  const { isSupported, isIOS, isSafari } = useNotificationCompatibility();
  const { isActive, updateLocalState, refreshState } = useNotificationStateManager();
  
  // Estado local solo para el loading del toggle
  const [isToggling, setIsToggling] = useState(false);
  
  // Ref para capturar el estado actual en timeouts
  const isActiveRef = useRef(isActive);

  // Debug: mostrar estado actual
  console.log('🔔 NotificationToggle - Estado actual:', { isActive, isToggling, userId: user?.uid });

  // Monitorear cambios en el estado
  useEffect(() => {
    console.log('🔍 NotificationToggle - Estado cambió:', { 
      isActive, 
      isToggling, 
      userId: user?.uid,
      timestamp: new Date().toISOString()
    });
    
    // Mantener el ref sincronizado
    isActiveRef.current = isActive;
  }, [isActive, isToggling, user?.uid]);

  const handleToggle = async () => {
    if (isToggling || !user?.uid) return;
    
    setIsToggling(true);
    
    try {
      if (isActive) {
        // Desactivar notificaciones
        await deactivateNotifications();
        
        // Actualizar estado en el contexto
        updateLocalState(false);
        
        // Guardar preferencia en localStorage
        saveNotificationPreference(false);
        
      } else {
        // Activar notificaciones
        const success = await activateNotifications();
        
        console.log('🔔 Resultado de activación:', success);
        
        // Actualizar estado en el contexto
        updateLocalState(success);
        
        // Verificar que el estado se actualizó
        console.log('🔍 Estado después de updateLocalState:', { 
          success, 
          isActive, 
          userId: user?.uid 
        });
        
        // Verificar que el estado se mantiene después de un breve delay
        setTimeout(() => {
          console.log('🔍 Estado después de 100ms:', { 
            isActive: isActiveRef.current, 
            userId: user?.uid,
            timestamp: new Date().toISOString()
          });
        }, 100);
        
        // Verificación inmediata del estado
        console.log('🔍 Verificación inmediata del estado:', { 
          success, 
          isActive, 
          isActiveRef: isActiveRef.current,
          userId: user?.uid 
        });
        
        // Guardar preferencia en localStorage
        if (success) {
          // No llamar a saveNotificationPreference aquí porque updateLocalState ya lo hace
          console.log('✅ Preferencia ya guardada por updateLocalState');
        }
      }
    } catch (error) {
      console.error('❌ Error en toggle:', error);
      // En caso de error, verificar estado real del backend
      setTimeout(refreshState, 1000);
    } finally {
      setIsToggling(false);
    }
  };

  // Permitir disparar el toggle desde fuera sin cambiar la lógica interna
  const externalToggleRef = useRef<() => void>(() => {});
  useEffect(() => {
    externalToggleRef.current = handleToggle;
  }, [handleToggle]);
  useEffect(() => {
    const listener = () => externalToggleRef.current();
    window.addEventListener('notification-toggle', listener);
    return () => {
      window.removeEventListener('notification-toggle', listener);
    };
  }, []);

  const activateNotifications = async (): Promise<boolean> => {
    try {
      console.log('🚀 Iniciando activación de notificaciones...');
      
      // Solicitar permisos del navegador
      if (typeof Notification !== 'undefined') {
        console.log('🔐 Solicitando permisos de notificaciones...');
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.log('❌ Permisos de notificaciones denegados');
          return false;
        }
        console.log('✅ Permisos de notificaciones concedidos');
      }
      
      // Esperar a que el Service Worker esté activo
      const swReady = await waitForServiceWorker();
      if (!swReady) {
        console.error('❌ Service Worker no está listo');
        return false;
      }
      
      // Obtener token FCM real usando Firebase con reintentos
      console.log('🔑 Obteniendo token FCM...');
      const token = await getRealFCMTokenWithRetry();
      if (!token) {
        console.error('❌ No se pudo obtener token FCM');
        return false;
      }
      
      console.log('✅ Token FCM obtenido exitosamente');
      
      // Enviar token al backend
      console.log('📡 Enviando token al backend...');
      if (user?.uid) {
        await sendTokenToBackend(token, user.uid, API_CONFIG.BASE_URL, headerService.getHeaders('authenticated'));
        console.log('✅ Token enviado al backend exitosamente');
        
        // Verificar que el token se guardó correctamente en el backend
        console.log('🔍 Verificando que el token se guardó en el backend...');
        const backendStatus = await checkBackendNotificationStatus(user.uid, API_CONFIG.BASE_URL, headerService.getHeaders('authenticated'));
        console.log('✅ Estado del backend después de guardar:', backendStatus);
        
        if (!backendStatus) {
          console.warn('⚠️ Token enviado pero backend no confirma el estado');
        }
      } else {
        throw new Error('No hay usuario disponible para enviar token');
      }
      
      return true;
    } catch (error) {
      console.error('❌ Error activando notificaciones:', error);
      return false;
    }
  };

  const getRealFCMTokenWithRetry = async (maxRetries = 3): Promise<string | null> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔑 Intento ${attempt}/${maxRetries} de obtener token FCM...`);
        
        // Importar Firebase Messaging dinámicamente
        const { getMessaging, getToken } = await import('firebase/messaging');
        const { initializeApp } = await import('firebase/app');
        
        // Configuración de Firebase (debería estar en tu app)
        const firebaseConfig = {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId: import.meta.env.VITE_FIREBASE_APP_ID
        };
        
        // Inicializar Firebase si no está ya inicializado
        let app;
        try {
          app = initializeApp(firebaseConfig);
        } catch (e) {
          // Firebase ya está inicializado
          app = undefined;
        }
        
        const messaging = getMessaging(app);
        
        // Obtener token FCM
        const token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY
        });
        
        if (token) {
          console.log('✅ Token FCM obtenido exitosamente en intento', attempt);
          return token;
        } else {
          console.warn('⚠️ Token FCM vacío en intento', attempt);
        }
        
      } catch (error) {
        console.error(`❌ Error en intento ${attempt} obteniendo token FCM:`, error);
        
        if (attempt === maxRetries) {
          console.error('❌ No se pudo obtener token FCM después de', maxRetries, 'intentos');
          return null;
        }
        
        // Esperar antes del siguiente intento
        const delay = 1000 * attempt; // 1s, 2s, 3s
        console.log(`⏳ Esperando ${delay}ms antes del siguiente intento...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    return null;
  };

  const deactivateNotifications = async (): Promise<void> => {
    try {
      console.log('🚫 Desactivando notificaciones...');
      
      // Enviar null al backend
      if (user?.uid) {
        await sendTokenToBackend(null, user.uid, API_CONFIG.BASE_URL, headerService.getHeaders('authenticated'));
        console.log('✅ Notificaciones desactivadas exitosamente');
      } else {
        throw new Error('No hay usuario disponible para desactivar notificaciones');
      }
      
    } catch (error) {
      console.error('❌ Error desactivando notificaciones:', error);
      throw error;
    }
  };

  // Función para guardar preferencias de notificaciones en localStorage
  // NOTA: Esta función ya no se usa directamente, updateLocalState se encarga de todo
  const saveNotificationPreference = (isEnabled: boolean) => {
    try {
      if (user?.uid) {
        // Solo guardar en localStorage sin cambiar el estado
        console.log('📱 Guardando preferencia en localStorage:', isEnabled);
        // Aquí podrías usar la utilidad directamente si fuera necesario
        // saveNotificationPreferences(isEnabled, user.uid);
      } else {
        console.warn('⚠️ No hay usuario disponible para guardar preferencias');
      }
    } catch (error) {
      console.error('❌ Error guardando preferencias en localStorage:', error);
    }
  };

  // Solo mostrar para waiters
  if (!isWaiter()) {
    return null;
  }

  // Solo mostrar si el navegador soporta notificaciones
  if (!isSupported) {
    return null;
  }

  // En iOS, mostrar advertencia sobre limitaciones
  if (isIOS && !isSafari) {
    console.warn('⚠️ iOS detectado pero no es Safari. Las notificaciones push pueden no funcionar correctamente.');
  }

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-10 h-10 text-lg';
      case 'lg':
        return 'w-14 h-14 text-2xl';
      default:
        return 'w-12 h-12 text-xl';
    }
  };

  const getIcon = () => {
    if (isToggling) {
      return '⏳';
    }
    
    if (isActive) {
      return '🔔';
    } else {
      return '🔕';
    }
  };

  const getTooltip = () => {
    if (isToggling) {
      return 'Configurando...';
    }
    
    if (isActive) {
      return 'Desactivar notificaciones';
    } else {
      return 'Activar notificaciones';
    }
  };

  const getStatusColor = () => {
    if (isToggling) {
      return 'bg-gray-100 text-gray-400';
    }
    
    if (isActive) {
      return 'bg-green-100 text-green-600 hover:bg-green-200';
    } else {
      return 'bg-red-100 text-red-600 hover:bg-red-200';
    }
  };

  const getStatusText = () => {
    if (isToggling) {
      return 'Configurando...';
    }
    
    if (isActive) {
      return 'Notificaciones activas';
    } else {
      return 'Notificaciones inactivas';
    }
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <button
        //onClick={handleToggle}
        disabled={isToggling}
        className={`aspect-square ${getSizeClasses()} ${getStatusColor()} rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
        title={getTooltip()}
        aria-label={getTooltip()}
      >
        {getIcon()}
      </button>
      
      {showLabel && (
        <span className="text-sm text-gray-600">
          {getStatusText()}
        </span>
      )}
      
      {/* Indicador de estado */}
      {isActive && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
      )}
      
      {!isActive && !isToggling && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-400 rounded-full border-2 border-white"></div>
      )}
      
      {/* Estado de carga */}
      {isToggling && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-400 rounded-full border-2 border-white animate-spin"></div>
      )}
    </div>
  );
};
export default NotificationToggle;

